import React, { useEffect, useState } from "react";

const ShopMathCalculator = ({ onSave }) => {
  const [display, setDisplay] = useState("0");
  const [history, setHistory] = useState("");
  const [angleMode, setAngleMode] = useState("DEG");

  const appendToDisplay = (value) => {
    if (display === "Error") {
      setDisplay(value);
      return;
    }

    if (display === "0") {
      if (["+", "-", "*", "/", "^", "%"].includes(value)) {
        setDisplay(display + value);
      } else {
        setDisplay(value);
      }
      return;
    }

    setDisplay((prev) => prev + value);
  };

  const handleClear = () => {
    setDisplay("0");
    setHistory("");
  };

  const handleDelete = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    setDisplay((prev) => (prev.length > 1 ? prev.slice(0, -1) : "0"));
  };

  const handlePlusMinus = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    if (display === "0") return;

    const lastOperatorMatch = display.match(/.*[+\-*/^(]/);
    if (!lastOperatorMatch) {
      if (display.startsWith("-")) {
        setDisplay(display.slice(1));
      } else {
        setDisplay(`-${display}`);
      }
      return;
    }

    const prefix = lastOperatorMatch[0];
    const suffix = display.slice(prefix.length);

    if (!suffix) {
      setDisplay((prev) => prev + "-");
      return;
    }

    if (suffix.startsWith("-")) {
      setDisplay(prefix + suffix.slice(1));
    } else {
      setDisplay(prefix + "-" + suffix);
    }
  };

  const handlePercent = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    try {
      const value = evaluateExpression(display, angleMode);
      const result = value / 100;
      setHistory(`${display}%`);
      setDisplay(formatResult(result));
    } catch {
      setDisplay("Error");
    }
  };

  const handleSquare = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    setDisplay((prev) => `(${prev})^2`);
  };

  const handleCube = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    setDisplay((prev) => `(${prev})^3`);
  };

  const handleReciprocal = () => {
    if (display === "Error") {
      setDisplay("0");
      return;
    }

    setDisplay((prev) => `1/(${prev})`);
  };

  const insertFunction = (fn) => {
    if (display === "Error") {
      setDisplay(`${fn}(`);
      return;
    }

    if (display === "0") {
      setDisplay(`${fn}(`);
      return;
    }

    setDisplay((prev) => prev + `${fn}(`);
  };

  const insertConstant = (constant) => {
    if (display === "Error") {
      setDisplay(constant);
      return;
    }

    if (display === "0") {
      setDisplay(constant);
      return;
    }

    setDisplay((prev) => prev + constant);
  };

  const handleCalculate = () => {
    try {
      const expression = display;
      const result = evaluateExpression(expression, angleMode);
      setHistory(expression);
      setDisplay(formatResult(result));
    } catch {
      setDisplay("Error");
    }
  };

  const handleSave = () => {
    if (!onSave) return;
    if (display === "Error") return;

    const equation = history || display;
    const answer = display;

    onSave({
      summary: `${equation} = ${answer}`,
      input: {
        equation,
        angleMode,
      },
      results: {
        answer,
      },
      warnings: [],
      confidence: "High",
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(display);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();

      if (
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }

      const { key } = event;

      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        appendToDisplay(key);
        return;
      }

      if (["+", "-", "*", "/", "(", ")", ".", "^"].includes(key)) {
        event.preventDefault();
        appendToDisplay(key);
        return;
      }

      if (key === "%") {
        event.preventDefault();
        handlePercent();
        return;
      }

      if (key === "Enter" || key === "=") {
        event.preventDefault();
        handleCalculate();
        return;
      }

      if (key === "Backspace") {
        event.preventDefault();
        handleDelete();
        return;
      }

      if (key === "Delete" || key === "Escape") {
        event.preventDefault();
        handleClear();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // Keyboard shortcuts intentionally bind to the current display/angle mode without rebinding every handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display, angleMode]);

  const buttonBaseStyle = {
    border: "1px solid var(--border-color)",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "16px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "var(--bg-surface-alt)",
    color: "var(--text-primary)",
  };

  const numberButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
  };

  const operatorButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--bg-surface-alt)",
    color: "var(--accent)",
  };

  const functionButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--bg-surface-alt)",
    color: "var(--text-primary)",
    fontSize: "14px",
  };

  const actionButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--bg-surface-alt)",
    color: "var(--text-secondary)",
  };

  const equalsButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--accent)",
    color: "#ffffff",
    border: "1px solid var(--accent)",
  };

  const saveButtonStyle = {
    ...buttonBaseStyle,
    background: "var(--accent)",
    color: "#ffffff",
    border: "1px solid var(--accent)",
    flex: 1,
  };

  const secondaryActionStyle = {
    ...buttonBaseStyle,
    flex: 1,
  };

  const renderButton = (label, onClick, style = numberButtonStyle) => (
    <button
      type="button"
      onClick={onClick}
      style={style}
      onMouseEnter={(e) => {
        e.currentTarget.style.filter = "brightness(1.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = "brightness(1)";
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      className="calculator-container"
      style={{
        background: "var(--bg-surface)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "24px",
      }}
    >
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          🧮 Shop Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Full-function calculator for machining, fabrication, and general shop math
        </p>
      </div>

      <div
        style={{
          maxWidth: "560px",
          margin: "0 auto",
          background: "var(--bg-surface-alt)",
          border: "1px solid var(--border-color)",
          borderRadius: "16px",
          padding: "20px",
        }}
      >
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ color: "var(--text-muted)", fontSize: "14px" }}>
            {history || "Ready"}
          </div>

          <button
            type="button"
            onClick={() =>
              setAngleMode((prev) => (prev === "DEG" ? "RAD" : "DEG"))
            }
            style={{
              ...functionButtonStyle,
              padding: "8px 12px",
              minWidth: "72px",
            }}
          >
            {angleMode}
          </button>
        </div>

        <div
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "18px",
            marginBottom: "18px",
            textAlign: "right",
            minHeight: "92px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              color: "var(--text-primary)",
              fontSize: "32px",
              fontWeight: 700,
              lineHeight: 1.2,
              wordBreak: "break-all",
            }}
          >
            {display}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "10px",
          }}
        >
          {renderButton("C", handleClear, actionButtonStyle)}
          {renderButton("⌫", handleDelete, actionButtonStyle)}
          {renderButton("(", () => appendToDisplay("("), operatorButtonStyle)}
          {renderButton(")", () => appendToDisplay(")"), operatorButtonStyle)}
          {renderButton("÷", () => appendToDisplay("/"), operatorButtonStyle)}

          {renderButton("sin", () => insertFunction("sin"), functionButtonStyle)}
          {renderButton("cos", () => insertFunction("cos"), functionButtonStyle)}
          {renderButton("tan", () => insertFunction("tan"), functionButtonStyle)}
          {renderButton("√", () => insertFunction("sqrt"), functionButtonStyle)}
          {renderButton("×", () => appendToDisplay("*"), operatorButtonStyle)}

          {renderButton("7", () => appendToDisplay("7"), numberButtonStyle)}
          {renderButton("8", () => appendToDisplay("8"), numberButtonStyle)}
          {renderButton("9", () => appendToDisplay("9"), numberButtonStyle)}
          {renderButton("^", () => appendToDisplay("^"), operatorButtonStyle)}
          {renderButton("-", () => appendToDisplay("-"), operatorButtonStyle)}

          {renderButton("4", () => appendToDisplay("4"), numberButtonStyle)}
          {renderButton("5", () => appendToDisplay("5"), numberButtonStyle)}
          {renderButton("6", () => appendToDisplay("6"), numberButtonStyle)}
          {renderButton("π", () => insertConstant("pi"), functionButtonStyle)}
          {renderButton("+", () => appendToDisplay("+"), operatorButtonStyle)}

          {renderButton("1", () => appendToDisplay("1"), numberButtonStyle)}
          {renderButton("2", () => appendToDisplay("2"), numberButtonStyle)}
          {renderButton("3", () => appendToDisplay("3"), numberButtonStyle)}
          {renderButton("1/x", handleReciprocal, functionButtonStyle)}
          {renderButton("=", handleCalculate, equalsButtonStyle)}

          {renderButton("±", handlePlusMinus, functionButtonStyle)}
          {renderButton("0", () => appendToDisplay("0"), numberButtonStyle)}
          {renderButton(".", () => appendToDisplay("."), numberButtonStyle)}
          {renderButton("%", handlePercent, functionButtonStyle)}
          {renderButton("Ans", () => {}, functionButtonStyle)}

          {renderButton("x²", handleSquare, functionButtonStyle)}
          {renderButton("x³", handleCube, functionButtonStyle)}
          {renderButton("log", () => insertFunction("log"), functionButtonStyle)}
          {renderButton("ln", () => insertFunction("ln"), functionButtonStyle)}
          {renderButton("exp", () => insertFunction("exp"), functionButtonStyle)}
        </div>

        <div
          style={{
            marginTop: "18px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button type="button" onClick={handleSave} style={saveButtonStyle}>
            💾 Save Calculation
          </button>
          <button type="button" onClick={copyToClipboard} style={secondaryActionStyle}>
            📄 Copy
          </button>
        </div>
      </div>
    </div>
  );
};

const formatResult = (value) => {
  if (!Number.isFinite(value)) {
    throw new Error("Invalid result");
  }

  const rounded = Math.round(value * 1000000000) / 1000000000;
  return rounded.toString();
};

const evaluateExpression = (expression, angleMode) => {
  const sanitized = expression.replace(/\s+/g, "");

  if (!sanitized) {
    return 0;
  }

  const transformed = sanitized
    .replace(/\^/g, "**")
    .replace(/\bpi\b/g, "Math.PI")
    .replace(/\bsqrt\(/g, "Math.sqrt(")
    .replace(/\blog\(/g, "Math.log10(")
    .replace(/\bln\(/g, "Math.log(")
    .replace(/\bexp\(/g, "Math.exp(")
    .replace(/\bsin\(/g, angleMode === "DEG" ? "sinDeg(" : "Math.sin(")
    .replace(/\bcos\(/g, angleMode === "DEG" ? "cosDeg(" : "Math.cos(")
    .replace(/\btan\(/g, angleMode === "DEG" ? "tanDeg(" : "Math.tan(");

  if (/[^0-9+\-*/().,A-Za-z_]/.test(transformed)) {
    throw new Error("Invalid characters");
  }

  const sinDeg = (x) => Math.sin((x * Math.PI) / 180);
  const cosDeg = (x) => Math.cos((x * Math.PI) / 180);
  const tanDeg = (x) => Math.tan((x * Math.PI) / 180);

  // Controlled calculator expression evaluator. Input is sanitized above to numbers, operators, parentheses, and approved function names.
  // eslint-disable-next-line no-new-func
  const result = Function(
    "Math",
    "sinDeg",
    "cosDeg",
    "tanDeg",
    `return (${transformed});`
  )(Math, sinDeg, cosDeg, tanDeg);

  if (!Number.isFinite(result)) {
    throw new Error("Invalid result");
  }

  return result;
};

export default ShopMathCalculator;
